import { useEffect, useMemo, useRef, useState } from 'react'
import { useClickOutside } from '@/lib/useClickOutside'
import { useAutosave } from '@/lib/useAutosave'
import {
  Activity as ActivityIcon,
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckSquare,
  ChevronDown,
  Clock,
  ExternalLink,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Phone,
  Settings2,
  Sparkles,
  Star,
  Trash2,
  X,
} from 'lucide-react'
import {
  useAddDealContact,
  useChangeDealContactRole,
  useDeal,
  useDealContacts,
  useDeleteDeal,
  usePipelineDetail,
  useRemoveDealContact,
  useSetDealPrimaryContact,
  useUpdateDeal,
} from '@/features/board/useBoard'
import { useContactSearch, useContacts, useCreateContact, useFieldDefinitions } from '@/features/contacts/useContacts'
import { useDealActivities, useDealTasks } from '@/features/activity/useActivities'
import { useComposerDraft } from '@/features/messaging/useComposerDraft'
import { useMembers } from '@/features/team/useTeam'
import { useAuthStore } from '@/features/auth/authStore'
import { ApiError } from '@/api/client'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { useDealContactRoles } from '@/features/agency/useDealContactRoles'
import type { Deal, FieldDefinition, Stage } from '@/api/types'
import type { UpdateDealParams } from '@/api/deals'
import type { Member } from '@/api/users'
import DealNextSteps from '@/components/DealNextSteps'
import DealNotes from '@/components/DealNotes'
import DealComposer from '@/components/DealComposer'
import DealActivity from '@/components/DealActivity'
import SaveState from '@/components/SaveState'
import Select from '@/components/ui/Select'
import SearchableSelect from '@/components/ui/SearchableSelect'
import OwnerCard from '@/components/OwnerCard'
import CustomFieldGroup from '@/components/CustomFieldGroup'
import ActionMenu from '@/components/ui/ActionMenu'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import DetailDrawer from '@/components/layout/DetailDrawer'
import Card from '@/components/layout/Card'
import StagePicker from '@/components/StagePicker'
import ListingCampaignCard from '@/features/campaigns/ListingCampaignCard'
import AttachmentsCard from '@/features/attachments/AttachmentsCard'
import { useCurrency } from '@/features/agency/useCurrency'
import FieldsTab from '@/features/settings/FieldsTab'
import { useAllFieldDefinitions } from '@/features/settings/useFieldsAdmin'
import {
  daysSince,
  formatDealValue,
  formatShortDate,
  isOverdue,
  isTaskOverdue,
  relativeDay,
} from '@/lib/dealFormat'
import { htmlToPlainText } from '@/lib/messageFormat'
import { dealHealth, HEALTH_BADGE, HEALTH_DOT, type DealHealth, type HealthTone } from '@/lib/dealHealth'

const fieldCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** Plain-text, length-capped preview of a rich-text activity body. */
function plainSnippet(html: string, max: number): string {
  // Paragraphs/line breaks become spaces, so "How are you?</p><p>Rusty" doesn't read "you?Rusty".
  const text = htmlToPlainText(html).replace(/\s+/g, ' ').trim()
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

/** Humanises an activity type ("stage_change" → "Stage change", "sms" → "SMS"). */
function humaniseActivity(type: string): string {
  if (type.toLowerCase() === 'sms') return 'SMS'
  const spaced = type.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export default function DealDetail({
  dealId,
  pipelineId,
  onClose,
  defaultExpanded,
}: {
  dealId: string
  pipelineId?: string
  onClose: () => void
  /** Open already full-screen (arrived via the record's own /deals/:id URL). */
  defaultExpanded?: boolean
}) {
  const { data: deal, isError } = useDeal(dealId)
  const { data: pipelineDetail } = usePipelineDetail(deal?.pipelineId)

  const stage = useMemo<Stage | null>(() => {
    if (!pipelineDetail || !deal) return null
    const all = [...pipelineDetail.sections.flatMap((s) => s.stages), ...pipelineDetail.looseStages]
    return all.find((s) => s.id === deal.stageId) ?? null
  }, [pipelineDetail, deal])

  return (
    <DetailDrawer
      onClose={onClose}
      defaultExpanded={defaultExpanded}
      width="w-[36rem] max-w-[95vw]"
      openInNewTabHref={`/deals/${dealId}`}
      title={
        deal ? (
          <div className="min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              {/* The deal's photo (house front, car) when it has one — it's how people recognise a listing. */}
              {deal.coverUrl ? (
                <img src={deal.coverUrl} alt="" className="w-16 h-12 rounded-lg object-cover border border-gray-100 shrink-0" />
              ) : (
                <Building2 size={18} className="text-gray-400 shrink-0" />
              )}
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-brand-darkest truncate">{deal.title}</h2>
                <div className="mt-1 flex flex-wrap items-center">
                  <StagePicker deal={deal} pipeline={pipelineDetail} stage={stage} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <h2 className="text-sm font-semibold text-brand-darkest">Deal</h2>
        )
      }
    >
      {(expanded) =>
        !deal ? (
          <div className="flex-1 p-4">
            {isError ? (
              // a deleted deal, one you can't see, or a link cut short (split over two lines in an email)
              <p className="text-sm text-gray-600">
                This deal can’t be opened — it may have been deleted, or the link is incomplete.{' '}
                <a href="/deals" className="text-brand-primary hover:underline">Go to Deals</a>
              </p>
            ) : (
              <p className="text-gray-400 text-sm">Loading…</p>
            )}
          </div>
        ) : (
          <DealBody key={deal.id} deal={deal} stage={stage} pipelineId={pipelineId} onClose={onClose} expanded={expanded} />
        )
      }
    </DetailDrawer>
  )
}

function DealBody({
  deal,
  stage,
  pipelineId,
  onClose,
  expanded,
}: {
  deal: Deal
  stage: Stage | null
  pipelineId?: string
  onClose: () => void
  expanded: boolean
}) {
  const dealId = deal.id
  const { data: fields } = useFieldDefinitions('deal')
  useAllFieldDefinitions()
  const update = useUpdateDeal(pipelineId)
  const del = useDeleteDeal(pipelineId)
  const { data: members } = useMembers()
  const role = useAuthStore((s) => s.user?.role)
  const isAgencyAdmin = role === 'AGENCY_ADMIN' // forms (and so off-market campaigns) are admin-managed
  const currency = useCurrency()
  // Any agent may hand a deal to a colleague (an assistant entering deals for an agent reassigns
  // them afterwards — the client's scenario). Managing custom FIELD definitions stays admin-only:
  // that's an agency-wide schema change, not a per-deal one. The backend only validates that the new
  // owner belongs to the agency, so this was a UI-only restriction.
  const canReassign = true
  const canManageFields = role === 'AGENCY_ADMIN' || role === 'PLATFORM_ADMIN'

  const { data: tasks } = useDealTasks(dealId)
  const { data: dealContacts } = useDealContacts(dealId)
  const { draft, update: updateDraft } = useComposerDraft(`deal:${dealId}`)

  const health = dealHealth({
    nextContactDate: deal.nextContactDate,
    stageEnteredAt: deal.stageEnteredAt,
    tasks: tasks ?? [],
  })

  // "SMS" / "Email" in the header bring the Send message card into view (it sits mid-page now).
  const composerRef = useRef<HTMLDivElement>(null)
  const showComposer = () => requestAnimationFrame(() => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
  const [title, setTitle] = useState(deal.title)
  const [value, setValue] = useState(deal.value != null ? String(deal.value) : '')
  const [ownerUserId, setOwnerUserId] = useState(deal.ownerUserId ?? '')
  const [custom, setCustom] = useState<Record<string, unknown>>(deal.custom ?? {})
  const [showFields, setShowFields] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [addTaskOpen, setAddTaskOpen] = useState(false)
  const [showAllActivity, setShowAllActivity] = useState(false)
  const [highlightTaskId, setHighlightTaskId] = useState<string | null>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  useClickOutside(moreRef, () => setMoreOpen(false), moreOpen)

  // After any server change to this deal (chiefly a save of these fields), resync the edit state to
  // the authoritative copy so `dirty` clears for EVERY field — immune to jsonb key-order or value
  // normalisation between what we send and what the server returns. React's "adjust state during
  // render" pattern, keyed on updatedAt: a background refetch of the same version doesn't clobber
  // in-progress edits, and (unlike a remount) the active tab is preserved.
  const [syncedAt, setSyncedAt] = useState(deal.updatedAt)
  if (deal.updatedAt !== syncedAt) {
    setSyncedAt(deal.updatedAt)
    setTitle(deal.title)
    setValue(deal.value != null ? String(deal.value) : '')
    setOwnerUserId(deal.ownerUserId ?? '')
    setCustom(deal.custom ?? {})
  }

  // Resolved from the owner being edited, not the saved one, so every surface that shows the owner
  // (header chip, compact snapshot tile) follows the picker immediately rather than waiting for Save.
  const ownerName = ownerUserId
    ? (members ?? []).find((m) => m.id === ownerUserId)?.fullName ?? null
    : null

  function setField(key: string, v: unknown) {
    setCustom((c) => ({ ...c, [key]: v }))
  }

  /**
   * "Open task" on a timeline entry: bring the Tasks card into view with that task ringed. The deal's
   * tasks are already loaded here, so this needs no fetch — and the ring is a flash, not a mode, so it
   * clears itself rather than leaving the card looking permanently selected.
   */
  function revealTask(taskId: string) {
    setHighlightTaskId(taskId)
  }
  useEffect(() => {
    if (!highlightTaskId) return
    const t = setTimeout(() => setHighlightTaskId(null), 2500)
    return () => clearTimeout(t)
  }, [highlightTaskId])

  const dirty =
    title !== deal.title ||
    value !== (deal.value != null ? String(deal.value) : '') ||
    ownerUserId !== (deal.ownerUserId ?? '') ||
    JSON.stringify(custom) !== JSON.stringify(deal.custom ?? {})

  /** The editable fields as the API wants them — shared by save, autosave and undo. */
  function paramsFrom(v: {
    title: string
    value: string
    ownerUserId: string
    custom: Record<string, unknown>
  }): UpdateDealParams {
    return {
      title: v.title,
      ownerUserId: v.ownerUserId || null,
      status: deal.status ?? null,
      value: v.value === '' ? null : Number(v.value),
      // not edited here — the next contact date follows the deal's tasks (the server derives it)
      nextContactDate: deal.nextContactDate ?? null,
      custom: v.custom,
    }
  }

  function save(done?: (ok: boolean) => void) {
    update.mutate(
      { id: dealId, params: paramsFrom({ title, value, ownerUserId, custom }) },
      {
        // Adopt the saved version WITHOUT replaying the server copy into the fields: with autosave a
        // request is often in flight while the user keeps typing, and the resync below would throw
        // away everything typed since it was sent.
        onSuccess: (saved) => {
          setSyncedAt(saved.updatedAt)
          done?.(true)
        },
        onError: () => done?.(false),
      },
    )
  }

  // Autosave the deal's own fields. There is nothing here a mistake can't be re-typed (a phone number
  // or an email address — the fields the CEO wanted confirmed — live on the contact, not the deal),
  // so the whole form saves itself and offers an Undo. The Save button stays for anyone who looks for it.
  const autosave = useAutosave({
    values: { title, value, ownerUserId, custom },
    dirty,
    capture: () =>
      paramsFrom({
        title: deal.title,
        value: deal.value != null ? String(deal.value) : '',
        ownerUserId: deal.ownerUserId ?? '',
        custom: deal.custom ?? {},
      }),
    save,
    restore: (params, done) => {
      setTitle(params.title ?? '')
      setValue(params.value != null ? String(params.value) : '')
      setOwnerUserId(params.ownerUserId ?? '')
      setCustom((params.custom ?? {}) as Record<string, unknown>)
      update.mutate(
        { id: dealId, params },
        {
          onSuccess: (saved) => {
            setSyncedAt(saved.updatedAt)
            done(true)
          },
          onError: () => done(false),
        },
      )
    },
  })

  const [confirmDelete, setConfirmDelete] = useState(false)

  const visibleFields = (fields ?? []).filter((f) => !f.hidden)
  const primary = deal.primaryContactId
    ? (dealContacts ?? []).find((c) => c.contactId === deal.primaryContactId)
    : undefined
  const primaryPhone = primary?.phone || primary?.mobile

  const nextSteps = (
    <DealNextSteps
      dealId={dealId}
      addingTask={addTaskOpen}
      onAddingTaskChange={setAddTaskOpen}
      highlightTaskId={highlightTaskId}
    />
  )
  const composer = <DealComposer dealId={dealId} draft={draft} updateDraft={updateDraft} />
  const contactsCard = <DealContactsSection dealId={dealId} primaryContactId={deal.primaryContactId} />
  const detailsForm = (
    <DealDetailsForm
      title={title}
      setTitle={setTitle}
      value={value}
      setValue={setValue}
      currency={currency}
      ownerUserId={ownerUserId}
      setOwnerUserId={setOwnerUserId}
      canReassign={canReassign}
      members={members}
      canManageFields={canManageFields}
      onManageFields={() => setShowFields(true)}
      fields={visibleFields}
      custom={custom}
      setField={setField}
    />
  )

  return (
    <>
      {!expanded && (
        <>
          <div className="flex-1 overflow-y-auto bg-gray-50/60 p-4">
            <div className="space-y-4">
              <DealSnapshotCompact deal={deal} stage={stage} currency={currency} ownerName={ownerName} health={health} />
              <DealAiSummary deal={deal} stageName={stage?.name ?? null} health={health} tasks={tasks} />
              {nextSteps}
              <Card title="Contacts">{contactsCard}</Card>
              {isAgencyAdmin && <ListingCampaignCard deal={deal} />}
              {composer}
              {/* Side panel mirrors the Overview: the 5 newest entries, expandable in place (the
                  compact panel has no tabs to send the user to). */}
              <Card
                title="Recent activity"
                action={
                  !showAllActivity && (
                    <button
                      onClick={() => setShowAllActivity(true)}
                      className="text-xs font-medium text-brand-primary hover:text-brand-dark"
                    >
                      View more →
                    </button>
                  )
                }
              >
                {showAllActivity ? (
                  <DealActivity dealId={dealId} onOpenTask={revealTask} />
                ) : (
                  <DealActivity dealId={dealId} limit={5} compact onOpenTask={revealTask} />
                )}
              </Card>
              <Card
                title="Deal details & custom fields"
                collapsible
                defaultOpen={false}
                action={
                  canManageFields && (
                    <button type="button" onClick={() => setShowFields(true)} title="Manage fields" className="text-gray-400 hover:text-brand-primary">
                      <Settings2 size={13} />
                    </button>
                  )
                }
              >
                {detailsForm}
              </Card>
              <AttachmentsCard target={{ kind: 'deal', id: dealId }} coverAttachmentId={deal.coverAttachmentId} collapsible />
            </div>
          </div>
          <div className="p-4 border-t border-gray-100 flex items-center justify-between bg-white">
            {/* Delete lives behind "⋯", not as the panel's only footer button. */}
            <ActionMenu
              label="More actions for this deal"
              items={[{ label: 'Delete deal', icon: <Trash2 size={15} />, danger: true, onClick: () => setConfirmDelete(true) }]}
            />
            {dirty ? (
              <button onClick={autosave.flush} disabled={update.isPending} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
                {update.isPending ? 'Saving…' : 'Save changes'}
              </button>
            ) : (
              <SaveState autosave={autosave} onRetry={autosave.flush} />
            )}
          </div>
        </>
      )}
      {expanded && (
      <>
      {/* Sub-header: chips + save state + quick actions */}
      <div className="border-b border-gray-100 bg-white px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm min-w-0">
          <span className="text-brand-dark font-medium">{formatDealValue(deal.value, currency)}</span>
          {/* Owner lives here, not only in Details: handing a deal to a colleague is a one-click
              action an agent does from the deal they're looking at. It edits the same draft state as
              the Details field, so the Save button in this bar clears for both. */}
          <span className="flex items-center gap-1 text-gray-500">
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 text-[8px] font-semibold">
              {ownerName ? ownerName.slice(0, 1).toUpperCase() : '—'}
            </span>
            {canReassign ? (
              <span className="relative inline-flex">
                <select
                  value={ownerUserId}
                  onChange={(e) => setOwnerUserId(e.target.value)}
                  title="Deal owner — pick a colleague, then Save changes"
                  className="appearance-none bg-transparent text-sm text-gray-500 pr-4 cursor-pointer rounded hover:text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                >
                  <option value="">Unassigned</option>
                  {(members ?? []).map((m) => (
                    <option key={m.id} value={m.id}>{m.fullName}</option>
                  ))}
                </select>
                <ChevronDown size={11} className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </span>
            ) : (
              ownerName ?? 'Unassigned'
            )}
          </span>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 flex items-center gap-1 ${HEALTH_BADGE[health.tone]}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${HEALTH_DOT[health.tone]}`} />
            {health.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          {dirty ? (
            <button
              onClick={autosave.flush}
              disabled={update.isPending}
              className="text-xs font-medium px-2.5 py-1 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-60"
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </button>
          ) : (
            <SaveState autosave={autosave} onRetry={autosave.flush} />
          )}
          {primaryPhone ? (
            <a href={`tel:${primaryPhone.replace(/[^\d+]/g, '')}`} className={quickBtnCls} title="Call the primary contact">
              <Phone size={13} /> Call
            </a>
          ) : (
            <button className={quickBtnCls} disabled title="No primary contact phone">
              <Phone size={13} /> Call
            </button>
          )}
          <button className={quickBtnCls} onClick={() => { updateDraft({ channel: 'sms' }); showComposer() }}>
            <MessageSquare size={13} /> SMS
          </button>
          <button className={quickBtnCls} onClick={() => { updateDraft({ channel: 'email' }); showComposer() }}>
            <Mail size={13} /> Email
          </button>
          <button className={quickBtnCls} onClick={() => setAddTaskOpen(true)}>
            <CheckSquare size={13} /> Add task
          </button>
          <div className="relative" ref={moreRef}>
            <button className={quickBtnCls} onClick={() => setMoreOpen((v) => !v)}>
              <MoreHorizontal size={13} /> More
            </button>
            {moreOpen && (
              <div className="absolute right-0 z-30 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg p-1">
                {canManageFields && (
                  <button
                    onClick={() => { setShowFields(true); setMoreOpen(false) }}
                    className="w-full text-left px-3 py-1.5 text-sm text-brand-dark hover:bg-gray-50 rounded-lg flex items-center gap-2"
                  >
                    <Settings2 size={13} /> Manage fields
                  </button>
                )}
                <button
                  onClick={() => { setConfirmDelete(true); setMoreOpen(false) }}
                  className="w-full text-left px-3 py-1.5 text-sm text-danger hover:bg-red-50 rounded-lg flex items-center gap-2"
                >
                  <Trash2 size={13} /> Delete deal
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* One page, HubSpot's record layout: what the deal IS on the left (every field, editable in
          place, plus its files), what HAPPENED in the middle (highlights, a note, a message, then the
          whole timeline — a new note lands on top of it), and what's CONNECTED on the right. No tabs:
          nothing about the deal is hidden behind one. */}
      <div className="flex-1 overflow-y-auto bg-gray-50/60 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[20rem_minmax(0,1fr)_22rem] gap-4 items-start">
          <div className="space-y-4 min-w-0 lg:order-3 xl:order-none lg:col-span-2 xl:col-span-1">
            <Card title="About this deal">{detailsForm}</Card>
            {/* File-type custom fields are edited with the rest in "About this deal" — no second card for them. */}
            <AttachmentsCard target={{ kind: 'deal', id: dealId }} coverAttachmentId={deal.coverAttachmentId} />
          </div>

          <div className="space-y-4 min-w-0 lg:order-1 xl:order-none">
            <DealSnapshot deal={deal} health={health} tasks={tasks} />
            <DealNotes dealId={dealId} />
            <div ref={composerRef}>{composer}</div>
            {/* History only — notes are written above and messages in Send message, so a note can never
                be confused with something actually sent to the client. */}
            <Card title="Activity">
              <DealActivity dealId={dealId} hideComposer onOpenTask={revealTask} />
            </Card>
          </div>

          <div className="space-y-4 min-w-0 lg:order-2 xl:order-none">
            <DealAiSummary deal={deal} stageName={stage?.name ?? null} health={health} tasks={tasks} />
            {nextSteps}
            <Card title="Contacts">{contactsCard}</Card>
            {isAgencyAdmin && <ListingCampaignCard deal={deal} />}
          </div>
        </div>
      </div>
      </>
      )}

      {confirmDelete && (
        <Modal
          title="Delete deal"
          tone="danger"
          icon={<AlertTriangle size={17} />}
          size="sm"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button type="button" onClick={() => setConfirmDelete(false)} className={modalButton.ghost}>Cancel</button>
              <button type="button" disabled={del.isPending} onClick={() => del.mutate(dealId, { onSuccess: onClose })} className={modalButton.danger}>
                {del.isPending ? 'Deleting…' : 'Delete deal'}
              </button>
            </>
          }
        >
          <p className="text-sm text-gray-600">
            Delete <span className="font-semibold text-brand-darkest">{deal.title}</span>? Its notes, tasks and timeline go too.
            The contacts stay. This can’t be undone.
          </p>
          {del.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(del.error)}</p>}
        </Modal>
      )}

      {showFields && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFields(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-brand-darkest">Manage deal fields</h2>
              <button onClick={() => setShowFields(false)} className="text-gray-400 hover:text-gray-600" title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <FieldsTab only="deal" />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const quickBtnCls =
  'flex items-center gap-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-transparent'

function SnapshotTile({
  label,
  icon,
  value,
  sub,
  alert,
}: {
  label: string
  icon?: React.ReactNode
  value: React.ReactNode
  sub?: React.ReactNode
  alert?: boolean
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
      <div className={`text-sm font-medium flex items-center gap-1.5 ${alert ? 'text-red-600' : 'text-brand-dark'}`}>
        {icon && <span className="text-gray-400 shrink-0">{icon}</span>}
        <span className="truncate">{value}</span>
      </div>
      {sub != null && <div className={`text-[11px] mt-0.5 ${alert ? 'text-red-500' : 'text-gray-400'}`}>{sub}</div>}
    </div>
  )
}

/** The actionable snapshot strip: next action, next contact, last activity, time in stage, deal age, open tasks. */
function DealSnapshot({ deal, health, tasks }: { deal: Deal; health: ReturnType<typeof dealHealth>; tasks: { dueAt: string | null; status: string; title: string; completedAt: string | null }[] | undefined }) {
  const { data: activities } = useDealActivities(deal.id)
  const openTasks = (tasks ?? []).filter((t) => t.status === 'open')
  const nextTask = useMemo(
    () => openTasks.filter((t) => t.dueAt).sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? ''))[0] ?? null,
    [openTasks],
  )
  const lastActivity = useMemo(
    () => (activities ?? []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null,
    [activities],
  )
  const daysInStage = daysSince(deal.stageEnteredAt)
  const dealAge = daysSince(deal.createdAt)
  const overdueCount = openTasks.filter((t) => t.dueAt && isTaskOverdue(t.dueAt)).length

  return (
    <Card
      title="Snapshot"
      bodyClassName="px-4 py-3"
      action={
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          Deal health
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${HEALTH_DOT[health.tone]}`} />
          <span className={`font-semibold rounded-full px-2 py-0.5 ${HEALTH_BADGE[health.tone]}`}>{health.label}</span>
        </span>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2.5">
        <SnapshotTile
          label="Next task"
          icon={<CheckSquare size={13} />}
          value={nextTask ? nextTask.title : '—'}
          sub={nextTask?.dueAt ? `Due ${formatShortDate(nextTask.dueAt)}` : undefined}
          alert={nextTask?.dueAt ? isTaskOverdue(nextTask.dueAt) : false}
        />
        <SnapshotTile
          label="Last activity"
          icon={<ActivityIcon size={13} />}
          value={lastActivity ? humaniseActivity(lastActivity.activityType) : deal.lastContactedAt ? 'Contacted' : 'None yet'}
          sub={lastActivity ? relativeDay(lastActivity.createdAt) : deal.lastContactedAt ? relativeDay(deal.lastContactedAt) : undefined}
        />
        <SnapshotTile
          label="Time in stage"
          icon={<Clock size={13} />}
          value={`${daysInStage} day${daysInStage === 1 ? '' : 's'}`}
          sub={`Entered ${daysInStage === 0 ? 'today' : formatShortDate(deal.stageEnteredAt)}`}
        />
        <SnapshotTile
          label="Deal age"
          icon={<CalendarClock size={13} />}
          value={`${dealAge} day${dealAge === 1 ? '' : 's'}`}
          sub={`Created ${formatShortDate(deal.createdAt)}`}
        />
        <SnapshotTile
          label="Open tasks"
          icon={<CheckSquare size={13} />}
          value={String(openTasks.length)}
          sub={overdueCount > 0 ? `${overdueCount} overdue` : undefined}
          alert={overdueCount > 0}
        />
      </div>
    </Card>
  )
}

/**
 * The 8-tile snapshot for the compact side panel (mockup #167): stage / value / owner / deal-health
 * on the first row, next action / next contact / last activity / time in stage on the second. In the
 * expanded workspace these core facts live in the sub-header, so {@link DealSnapshot} omits them.
 */
function DealSnapshotCompact({
  deal,
  stage,
  currency,
  ownerName,
  health,
}: {
  deal: Deal
  stage: Stage | null
  currency: string
  ownerName: string | null
  health: ReturnType<typeof dealHealth>
}) {
  const { data: tasks } = useDealTasks(deal.id)
  const { data: activities } = useDealActivities(deal.id)
  const nextTask = (tasks ?? [])
    .filter((t) => t.status === 'open' && t.dueAt)
    .sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? ''))[0]
  const lastActivity = (activities ?? []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  const daysInStage = daysSince(deal.stageEnteredAt)

  return (
    <Card title="Snapshot">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-4">
        <SnapshotTile
          label="Stage"
          value={
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: stage?.color ?? '#0ea5e9' }} />
              {stage?.name ?? '—'}
            </span>
          }
        />
        <SnapshotTile label="Value" value={formatDealValue(deal.value, currency)} />
        <SnapshotTile label="Owner" value={ownerName ?? 'Unassigned'} />
        <SnapshotTile
          label="Deal health"
          value={<span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${HEALTH_BADGE[health.tone]}`}>{health.label}</span>}
        />
        <SnapshotTile
          label="Next task"
          icon={<CheckSquare size={13} />}
          value={nextTask ? nextTask.title : '—'}
          sub={nextTask?.dueAt ? `Due ${formatShortDate(nextTask.dueAt)}` : undefined}
          alert={nextTask?.dueAt ? isTaskOverdue(nextTask.dueAt) : false}
        />
        <SnapshotTile
          label="Last activity"
          icon={<ActivityIcon size={13} />}
          value={lastActivity ? humaniseActivity(lastActivity.activityType) : deal.lastContactedAt ? 'Contacted' : 'None yet'}
          sub={lastActivity ? relativeDay(lastActivity.createdAt) : deal.lastContactedAt ? relativeDay(deal.lastContactedAt) : undefined}
        />
        <SnapshotTile
          label="Time in stage"
          icon={<Clock size={13} />}
          value={`${daysInStage} day${daysInStage === 1 ? '' : 's'}`}
          sub={`Since ${formatShortDate(deal.stageEnteredAt)}`}
        />
      </div>
    </Card>
  )
}

/** Signal-line text colour per health tone (leads the summary; recap stays muted). */
const SIGNAL_TEXT: Record<HealthTone, string> = {
  red: 'text-red-700',
  amber: 'text-amber-700',
  gray: 'text-gray-600',
  green: 'text-emerald-700',
}

/**
 * Rule-based deal digest (no AI backend). Instead of restating the Snapshot tiles beside it, it
 * LEADS with what the reader should act on — derived from {@link dealHealth} — then a muted one-line
 * recap for context. This is what's useful to the owning rep; the plain recap alone just duplicates
 * the tiles.
 */
function DealAiSummary({
  deal,
  stageName,
  health,
  tasks,
}: {
  deal: Deal
  stageName: string | null
  health: DealHealth
  tasks: { dueAt: string | null; status: string; title: string; completedAt: string | null }[] | undefined
}) {
  const { data: activities } = useDealActivities(deal.id)
  const openTasks = (tasks ?? []).filter((t) => t.status === 'open')
  const overdueTasks = openTasks.filter((t) => t.dueAt && isTaskOverdue(t.dueAt))
  const lastActivity = (activities ?? []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  // The latest note is the freshest thing anyone actually said about the deal — the client asked for
  // exactly this ("it'll just say deal notes… only the last bit of information").
  const latestNote = (activities ?? [])
    .filter((a) => a.activityType === 'note')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  const noteText = latestNote ? plainSnippet(latestNote.body, 150) : null
  const dealAge = daysSince(deal.createdAt)
  const daysInStage = daysSince(deal.stageEnteredAt)
  const contactRel = deal.lastContactedAt
    ? `last contacted ${relativeDay(deal.lastContactedAt)}`
    : lastActivity
      ? `last activity ${relativeDay(lastActivity.createdAt)}`
      : 'no activity yet'

  // The action-first headline, from the health signal.
  let signal: string
  switch (health.key) {
    case 'overdue': {
      const bits: string[] = []
      if (deal.nextContactDate && isOverdue(deal.nextContactDate)) bits.push('follow-up overdue')
      if (overdueTasks.length) bits.push(`${overdueTasks.length} task${overdueTasks.length === 1 ? '' : 's'} overdue`)
      signal = `Needs attention — ${bits.length ? bits.join(', ') : 'overdue items'}.`
      break
    }
    case 'due_soon':
      signal = deal.nextContactDate ? `Follow-up due ${relativeDay(deal.nextContactDate)}.` : 'A task is due soon.'
      break
    case 'no_follow_up':
      signal = 'No follow-up scheduled — set the next step.'
      break
    case 'stalled':
      signal = `Stalled — ${daysInStage} days in ${stageName ?? 'this stage'} with no upcoming follow-up.`
      break
    default:
      signal = deal.nextContactDate ? `On track — next follow-up ${relativeDay(deal.nextContactDate)}.` : 'On track.'
  }

  // Muted context recap — the neutral facts, one line.
  const recap = [
    `Created ${dealAge}d ago`,
    contactRel,
    openTasks.length
      ? `${openTasks.length} open task${openTasks.length === 1 ? '' : 's'}${overdueTasks.length ? ` (${overdueTasks.length} overdue)` : ''}`
      : 'no open tasks',
    daysInStage === 0 ? `just entered ${stageName ?? 'stage'}` : `in ${stageName ?? 'stage'} ${daysInStage}d`,
  ].join(' · ')

  return (
    <section className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
      <h3 className="text-sm font-semibold text-brand-darkest flex items-center gap-1.5 mb-1.5">
        <Sparkles size={14} className="text-indigo-500" /> AI Deal Summary
      </h3>
      <p className={`text-sm font-medium flex items-start gap-1.5 ${SIGNAL_TEXT[health.tone]}`}>
        <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${HEALTH_DOT[health.tone]}`} />
        <span>{signal}</span>
      </p>
      {noteText && (
        <p className="text-xs text-brand-dark mt-1">
          <span className="text-gray-400">Latest note:</span> {noteText}
        </p>
      )}
      <p className="text-xs text-gray-500 mt-1">{recap}</p>
      <p className="text-[11px] text-gray-400 mt-1.5">
        Auto-generated from this deal's data — richer AI summaries arrive with the AI assistant.
      </p>
    </section>
  )
}

function DealDetailsForm({
  title,
  setTitle,
  value,
  setValue,
  currency,
  ownerUserId,
  setOwnerUserId,
  canReassign,
  members,
  canManageFields,
  onManageFields,
  fields,
  custom,
  setField,
}: {
  title: string
  setTitle: (v: string) => void
  value: string
  setValue: (v: string) => void
  currency: string
  ownerUserId: string
  setOwnerUserId: (v: string) => void
  canReassign: boolean
  members: Member[] | undefined
  canManageFields: boolean
  onManageFields: () => void
  fields: FieldDefinition[]
  custom: Record<string, unknown>
  setField: (key: string, v: unknown) => void
}) {
  const isDate = (t: string) => t === 'date' || t === 'datetime'
  const dateFields = fields.filter((f) => isDate(f.dataType))
  const otherFields = fields.filter((f) => !isDate(f.dataType))

  return (
    <div className="space-y-4">
      {/* One column: this form sits in the deal's narrow "About" column as well as the side panel. */}
      <div className="grid grid-cols-1 gap-3">
        <Labeled label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={fieldCls} />
        </Labeled>
        <Labeled label="Value">
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} className={fieldCls} />
          {value !== '' && Number.isFinite(Number(value)) && (
            <p className="text-[11px] text-gray-400 mt-1">{formatDealValue(Number(value), currency)}</p>
          )}
        </Labeled>
        <Labeled label="Deal owner">
          {canReassign ? (
            <>
              <SearchableSelect
                size="md"
                value={ownerUserId}
                onChange={setOwnerUserId}
                placeholder="Unassigned"
                // Just names; the email only tells apart two people with the same name (e.g. two "Rusty" logins).
                options={[{ value: '', label: 'Unassigned' }, ...(members ?? []).map((u) => ({
                  value: u.id,
                  label: u.email && (members ?? []).some((o) => o.id !== u.id && o.fullName === u.fullName) ? `${u.fullName} (${u.email})` : u.fullName,
                }))]}
                className="w-full"
              />
            </>
          ) : ownerUserId ? (
            <OwnerCard member={members?.find((u) => u.id === ownerUserId)} inline />
          ) : (
            <p className="px-3 py-2 text-sm text-gray-400 bg-gray-50 border border-gray-100 rounded-lg">Unassigned</p>
          )}
        </Labeled>
      </div>
      {dateFields.length > 0 && (
        <div className="pt-3 border-t border-gray-100">
          <CustomFieldGroup title="Dates" fields={dateFields} custom={custom} setField={setField} />
        </div>
      )}
      {otherFields.length > 0 && (
        <div className="pt-3 border-t border-gray-100">
          <CustomFieldGroup title="Additional details" fields={otherFields} custom={custom} setField={setField} collapsibleAfter={6} />
        </div>
      )}
      {/* Said in words where the fields are changed — a gear icon is easy to miss. Opens over the deal. */}
      {canManageFields && (
        <button
          type="button"
          onClick={onManageFields}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline"
        >
          <Settings2 size={12} /> Add or edit fields
        </button>
      )}
    </div>
  )
}

function DealContactsSection({ dealId, primaryContactId }: { dealId: string; primaryContactId: string | null }) {
  const roleOptions = useDealContactRoles()
  const { data: links } = useDealContacts(dealId)
  const addLink = useAddDealContact(dealId)
  const removeLink = useRemoveDealContact(dealId)
  const changeRole = useChangeDealContactRole(dealId)
  const setPrimary = useSetDealPrimaryContact(dealId)
  const [roleError, setRoleError] = useState<string | null>(null)

  const [contactQuery, setContactQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(contactQuery.trim()), 300)
    return () => clearTimeout(t)
  }, [contactQuery])
  const { data: firstPage } = useContacts()
  const { data: searchPage } = useContactSearch(debouncedQuery)
  const contactsPage = debouncedQuery ? searchPage : firstPage

  const [linkContactId, setLinkContactId] = useState('')
  const [pickedOption, setPickedOption] = useState<{ value: string; label: string } | null>(null)
  const [linkRoleRaw, setLinkRole] = useState(roleOptions.includes('contact') ? 'contact' : roleOptions[0])
  const [linkError, setLinkError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const linkRole = roleOptions.includes(linkRoleRaw) ? linkRoleRaw : roleOptions[0]
  // Creating a contact without leaving the deal: previously the contact had to exist first, so adding
  // someone mid-call meant abandoning the deal screen.
  const createContact = useCreateContact()
  const [creating, setCreating] = useState(false)
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', email: '', mobile: '' })

  function createAndAttach() {
    const { firstName, lastName, email, mobile } = newContact
    if (!firstName.trim() && !lastName.trim() && !email.trim()) return
    setLinkError(null)
    createContact.mutate(
      {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        mobile: mobile.trim() || undefined,
      },
      {
        onSuccess: ({ contact }) =>
          addLink.mutate(
            { contactId: contact.id, role: linkRole },
            {
              onSuccess: () => {
                setNewContact({ firstName: '', lastName: '', email: '', mobile: '' })
                setCreating(false)
                setAdding(false)
              },
              onError: (e) => setLinkError(apiErrorMessage(e)),
            },
          ),
        onError: (e) => setLinkError(apiErrorMessage(e)),
      },
    )
  }

  const contactOptions = useMemo(() => {
    const contacts = contactsPage?.items ?? []
    const nameOf = (c: (typeof contacts)[number]) => [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || c.id
    const options = contacts.map((c) => {
      const n = nameOf(c)
      return { value: c.id, label: c.email && c.email !== n ? `${n} (${c.email})` : n }
    })
    if (pickedOption && !options.some((o) => o.value === pickedOption.value)) options.push(pickedOption)
    return options
  }, [contactsPage, pickedOption])

  function pickContact(id: string) {
    setLinkContactId(id)
    setPickedOption(contactOptions.find((o) => o.value === id) ?? null)
  }

  function attach() {
    if (!linkContactId) return
    setLinkError(null)
    addLink.mutate(
      { contactId: linkContactId, role: linkRole },
      {
        onSuccess: () => {
          setLinkContactId('')
          setPickedOption(null)
          setAdding(false)
        },
        onError: (e) =>
          setLinkError(
            e instanceof ApiError && e.code === 'deal.contact_link_exists'
              ? 'This contact already has that role on the deal.'
              : apiErrorMessage(e),
          ),
      },
    )
  }

  const rows = links ?? []

  return (
    <div>
      <div className="flex items-center justify-end -mt-1 mb-2">
        <button type="button" onClick={() => setAdding((v) => !v)} className="text-xs text-brand-primary flex items-center gap-1 hover:text-brand-dark">
          + Add contact
        </button>
      </div>
      {rows.length === 0 && !adding && <p className="text-xs text-gray-400">No contacts linked yet.</p>}
      <div className="space-y-3">
        {rows.map((l) => {
          const isPrimary = l.contactId === primaryContactId
          const name = [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email || '—'
          const initials = ((l.firstName?.[0] ?? '') + (l.lastName?.[0] ?? '')).toUpperCase() || (l.email?.[0] ?? '?').toUpperCase()
          return (
            <div key={`${l.contactId}-${l.role}`} className="flex items-start gap-2.5">
              <span className={`shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatarColor(l.contactId)}`}>
                {initials}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <a
                    href={`/contacts/${l.contactId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open contact in a new tab"
                    className="text-sm font-medium text-brand-dark hover:text-brand-primary hover:underline text-left inline-flex items-center gap-1"
                  >
                    {name}
                    <ExternalLink size={11} className="text-gray-400 shrink-0" />
                  </a>
                  <RoleChipSelect
                    value={l.role}
                    options={roleOptions}
                    disabled={changeRole.isPending}
                    onChange={(next, revert) => {
                      setRoleError(null)
                      changeRole.mutate(
                        { contactId: l.contactId, currentRole: l.role, role: next },
                        {
                          onError: (e) => {
                            revert()
                            setRoleError(
                              e instanceof ApiError && e.code === 'deal.contact_link_exists'
                                ? 'This contact already has that role on the deal.'
                                : apiErrorMessage(e),
                            )
                          },
                        },
                      )
                    }}
                  />
                  {isPrimary && <span className="text-[10px] uppercase text-brand-primary font-semibold">Primary</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                  {(l.phone || l.mobile) && (
                    <a href={`tel:${(l.phone || l.mobile || '').replace(/[^\d+]/g, '')}`} className="flex items-center gap-1 hover:text-brand-primary">
                      <Phone size={11} /> {l.phone || l.mobile}
                    </a>
                  )}
                  {l.email && (
                    <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-brand-primary truncate">
                      <Mail size={11} /> {l.email}
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 text-gray-300">
                {(l.phone || l.mobile) && (
                  <a href={`tel:${(l.phone || l.mobile || '').replace(/[^\d+]/g, '')}`} className="hover:text-brand-primary" title="Call"><Phone size={14} /></a>
                )}
                {l.email && <a href={`mailto:${l.email}`} className="hover:text-brand-primary" title="Email"><Mail size={14} /></a>}
                <button
                  onClick={() => setPrimary.mutate(l.contactId)}
                  disabled={setPrimary.isPending || isPrimary}
                  className={isPrimary ? 'text-brand-primary' : 'hover:text-brand-primary'}
                  title={isPrimary ? 'Primary contact' : 'Set as primary'}
                >
                  <Star size={14} fill={isPrimary ? 'currentColor' : 'none'} />
                </button>
                <button onClick={() => removeLink.mutate(l.contactId)} disabled={removeLink.isPending} className="hover:text-danger" title="Remove">
                  <X size={14} />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {adding && !creating && (
        <div className="mt-3 space-y-1.5">
          <div className="flex gap-2">
            <SearchableSelect size="xs" value={linkContactId} onChange={pickContact} placeholder="— contact —" options={contactOptions} onSearchChange={setContactQuery} className="flex-1 min-w-0" />
            <Select size="xs" value={linkRole} onChange={(e) => setLinkRole(e.target.value)}>
              {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
            <button onClick={attach} disabled={!linkContactId || addLink.isPending} className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60">
              Add
            </button>
          </div>
          <button type="button" onClick={() => setCreating(true)} className="text-xs text-brand-primary hover:text-brand-dark">
            + Create a new contact
          </button>
        </div>
      )}

      {adding && creating && (
        <div className="mt-3 space-y-2 rounded-lg border border-gray-200 p-2.5">
          <div className="grid grid-cols-2 gap-2">
            <input autoFocus value={newContact.firstName} onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })} placeholder="First name" className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
            <input value={newContact.lastName} onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })} placeholder="Last name" className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
            <input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" type="email" className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
            <input value={newContact.mobile} onChange={(e) => setNewContact({ ...newContact, mobile: e.target.value })} placeholder="Mobile" className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40" />
          </div>
          <div className="flex items-center gap-2">
            <Select size="xs" value={linkRole} onChange={(e) => setLinkRole(e.target.value)}>
              {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
            <button
              onClick={createAndAttach}
              disabled={createContact.isPending || addLink.isPending}
              className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
            >
              {createContact.isPending || addLink.isPending ? 'Adding…' : 'Create & add'}
            </button>
            <button type="button" onClick={() => setCreating(false)} className="text-xs text-gray-500 hover:text-brand-dark">
              Back
            </button>
          </div>
          <p className="text-[11px] text-gray-400">An existing contact with the same email is reused, not duplicated.</p>
        </div>
      )}
      {linkError && <p className="text-danger text-xs mt-1">{linkError}</p>}
      {roleError && <p className="text-danger text-xs mt-1">{roleError}</p>}
    </div>
  )
}

/**
 * The role badge, editable in place: picking another role re-labels the link. Previously the only
 * way to fix a role was to remove the contact and add it back, which lost the link and could move
 * the deal's primary contact. A role the agency has since dropped from its list is kept as an
 * option so the current value still renders — those are exactly the links worth correcting.
 */
function RoleChipSelect({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string
  options: string[]
  disabled: boolean
  /** Calls `revert` if the change was rejected, so the chip drops back to the real role. */
  onChange: (role: string, revert: () => void) => void
}) {
  // Show the picked role while the request is in flight — otherwise the chip snaps back to the old
  // one until the refetch lands, which reads as "it didn't save". Cleared when the server value
  // catches up (React's adjust-state-during-render) or when the parent reverts a rejected change.
  const [picked, setPicked] = useState<string | null>(null)
  const [seen, setSeen] = useState(value)
  if (value !== seen) {
    setSeen(value)
    setPicked(null)
  }
  const shown = picked ?? value
  const choices = options.includes(shown) ? options : [shown, ...options]
  return (
    <span className="relative inline-flex">
      <select
        value={shown}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value
          if (next === shown) return
          setPicked(next)
          onChange(next, () => setPicked(null))
        }}
        title="Change this contact's role on the deal"
        className="appearance-none text-[10px] uppercase bg-gray-100 text-gray-500 rounded pl-1.5 pr-4 py-0.5 cursor-pointer hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/40 disabled:opacity-60 disabled:cursor-default"
      >
        {choices.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <ChevronDown size={9} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </span>
  )
}

/** Deterministic avatar tint from a contact id, so the same contact keeps its colour. */
function avatarColor(id: string): string {
  const palette = [
    'bg-violet-100 text-violet-600',
    'bg-teal-100 text-teal-600',
    'bg-emerald-100 text-emerald-600',
    'bg-amber-100 text-amber-600',
    'bg-sky-100 text-sky-600',
    'bg-rose-100 text-rose-600',
  ]
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return palette[h % palette.length]
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  )
}
